// How the sessions' pages name things: a session's state and how it ended,
// who sent a prompt and who started it. And an issue's sessions.
import type { Actor, Me, Session, SessionEnd, SessionState } from '../api/types'
import { actorName } from '../vocab'

export const SESSION_STATE_NAMES: Record<SessionState, string> = {
  starting: 'Starting',
  running: 'Running',
  idle: 'Waiting for a prompt',
  ended: 'Ended',
}

export const OUTCOME_NAMES: Record<SessionEnd['outcome'], string> = {
  succeeded: 'Succeeded',
  failed: 'Failed',
  unknown: 'Outcome unknown',
  interrupted: 'Interrupted',
}

/** A session's state, in words; one these pages do not know yet, as the server names it. */
export function stateName(state: SessionState): string {
  return SESSION_STATE_NAMES[state] ?? state
}

/** How a session ended, in a few words: its outcome, and why when the server says. */
export function endedText(ended: SessionEnd): string {
  const outcome = OUTCOME_NAMES[ended.outcome] ?? OUTCOME_NAMES.unknown
  const reason = ended.reason?.trim()
  return reason ? `${outcome}: ${reason}` : outcome
}

/** Who sent a prompt: the person the pages act as by their name, anyone else as the board names them. */
export function senderName(by: Actor, me: Me | null): string {
  if (by.kind === 'person' && me?.member && by.member === me.member.id) return me.member.name
  return actorName(by)
}

/** Who started a session: the person the pages act as by their name, anyone else as a person. */
export function starterName(memberId: string, me: Me | null): string {
  return senderName({ kind: 'person', member: memberId }, me)
}

/** Newest first, by when each started, then by id. */
const newestFirst = (a: Session, b: Session) => b.created_at.localeCompare(a.created_at) || (a.id < b.id ? 1 : a.id > b.id ? -1 : 0)

/** An issue's sessions, newest first. */
export function sessionsOf(sessions: ReadonlyMap<string, Session>, issueId: string): Session[] {
  return [...sessions.values()].filter((s) => s.issue_id === issueId).sort(newestFirst)
}
